import type {FunctionComponent, SyntheticEvent} from 'react'
import React, {Fragment} from 'react'
import clsx from 'clsx'
import {useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import type {Product, RootAndListContext, RootLoaderResponse, SlugApiResponse} from '~/types'
import {getTranslation} from '~/localization/translation'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {objectToQueryString} from '~/utils/commons'
import {useReportItemSeen, useReportWidgetClick, useSendPartnerProductClickout} from '~/hooks/analytic-hooks'
import {FeedbackStar} from '~/icons/product-page/feedback-star'
import {IconProductBrand} from '~/icons/icon-product-brand'
import {AmazonLogoIcon} from '~/icons/amazon-logo-icon'
import {getDomainConfig} from '~/domains'
import {grantAllConsent} from '~/utils/consent-manager.client'
import {cssDomains} from '~/utils/constants'
import CustomerHighlightsBeyond from '~/components/commons/customer-insights-beyond'
import {DiscountBadgeReorganized} from '../badges/discount-badge-reorganized'
import ProductCardBadge from '../badges/card-placement-badge'
import ListingsCta from '../category-products/listings-cta'
import SaleEventBadge from '../badges/saleEventBadge'
import DefaultCta from './default-cta'

export interface TopPicksUpdatedCardProps {
  index: number
  product: Product
  showProductPageLinks?: boolean
}

const dealsBadgesByPlacement = {
  0: 'valueForMoney',
  1: 'greatChoice',
} as Record<number, string>

const TopPicksUpdatedCard: FunctionComponent<TopPicksUpdatedCardProps> = ({
  product,
  index,
  showProductPageLinks = true,
}) => {
  const {locale, featureFlags, language, trackAnalytics, domain, routeName} = useRouteLoaderData(
    'root',
  ) as RootLoaderResponse
  const {
    clickId,
    setShowCookieConsentGdpr,
    dataResponse: {productsCategory} = {} as SlugApiResponse,
  } = useOutletContext<RootAndListContext>()
  const translations = getTranslation(language)
  const submit = useSubmit()
  const {domainName, styles: stylesBydomain, siteName, domainType} = getDomainConfig(domain, locale)

  const {
    name,
    badge,
    asin,
    discount,
    brand,
    image,
    coupon,
    affiliateLink,
    isSellerProduct,
    numOfRatings,
    chosenBy,
    brief,
    price,
    customerHighlights,
    storeListings,
  } = product

  const mergedBriefParts = brief?.length ? brief.join('') : name
  const scoreFormatter = new Intl.NumberFormat(language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })

  const productScore = scoreFormatter.format(product.listScore)

  const affiliateLinkWithClickId = affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId)
  const shouldForceTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )

  const storeListingsData = [storeListings?.amazon ?? [], storeListings?.ebay ?? []].flatMap((listing) => listing)
  const showBuyingOptionsCta = cssDomains.includes(domainName) && storeListings && storeListingsData?.length > 0

  const trackSearchParams = objectToQueryString({
    session_id: trackAnalytics?.session_id,
    ...trackAnalytics?.pageLoadData,
    page_id: trackAnalytics?.page_id,
    user_id: trackAnalytics?.user_id,
  })
  const redirectUrl = shouldForceTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  const reportClickout = useSendPartnerProductClickout({
    product,
    index: index + 1,
    locale,
    listName: 'category-products',
    categorySlug: productsCategory?.slug,
    categoryName: productsCategory?.name,
  })
  const reportWidgetClick = useReportWidgetClick()
  const [cardRef] = useReportItemSeen({
    item_list_id: productsCategory?.slug,
    item_name: name,
    item_source: 'list',
    partner_item_brand: brand ?? '',
    partner_item_id: asin,
    affiliation: affiliateLink.type,
    is_dws: isSellerProduct,
    price: price?.amount,
    currency: price?.currency,
    click_element_name: 'top-picks',
    index: index + 1,
  })
  const handleCardClick = (_e: SyntheticEvent) => {
    if (shouldForceTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()
      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout()
    reportWidgetClick({
      widget_name: 'top-picks-card',
      widget_place: `card-${index + 1}`,
    })

    if (typeof Storage != 'undefined') {
      localStorage.setItem('clickTime', Date.now().toString())
      if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
    }
  }
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Review',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: `${+productScore}`,
              bestRating: 10,
              worstRating: 0,
            },
            author: {
              '@type': 'Organization',
              name: siteName,
            },
            reviewBody: `${mergedBriefParts}`,
            itemReviewed: {
              '@type': 'Product',
              name: `${name}`,
              review: {
                '@context': 'https://schema.org/',
                '@type': 'Review',
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: `${+productScore}`,
                  bestRating: 10,
                  worstRating: 0,
                },
                author: {
                  '@type': 'Organization',
                  name: siteName,
                },
                reviewBody: `${mergedBriefParts}`,
              },
            },
          }),
        }}
      ></script>

      <div className="flex flex-col gap-4 transition-all duration-300 ease-in-out tablet:gap-3">
        <ProductCardBadge
          className={clsx(
            'z-40 flex h-full w-fit items-center justify-center overflow-hidden rounded-lg',
            '[&>div>p]:px-3 [&>div>span]:text-xl [&>div>span]:font-semibold',
          )}
          index={index}
          badge={domainType === 'deals' ? dealsBadgesByPlacement[index] : badge}
          coupon={coupon}
        />

        <h3 className="line-clamp-2 text-[20px] font-normal capitalize text-[#363636] tablet:text-base tablet:font-medium">
          {name}
        </h3>

        {/* -------------------------------------------  INFO CARD   --------------------------------------------- */}

        <div
          ref={cardRef}
          className={clsx(
            'border-gray relative flex h-full min-h-[300px] w-full flex-row rounded-3xl border bg-white shadow-product-page-section',
            'tablet:flex-col tablet:items-center tablet:justify-end tablet:gap-3 tablet:rounded-xl tablet:py-5',
            'overflow-hidden',
          )}
        >
          {/* ------------------------------------------- Score Mobile --------------------------------------------- */}

          <a
            href={redirectUrl}
            onClick={handleCardClick}
            aria-label={`view ${brand}`}
            target="_blank"
            rel="sponsored noreferrer"
            className={clsx(
              'absolute flex flex-row items-center gap-2 desktop:hidden',
              showBuyingOptionsCta ? 'left-1/2 top-2 -translate-x-1/2' : 'left-5 top-4',
            )}
          >
            <FeedbackStar className="size-4 fill-yellow-450" />
            <p className="text-base font-normal tracking-wide">
              {productScore}&nbsp;
              {translations.ProductCard.scoreSuffix}
            </p>
          </a>

          {/* ------------------------------------------- Discount Badge --------------------------------------------- */}

          {!!discount && !showBuyingOptionsCta ? (
            <DiscountBadgeReorganized
              className={clsx(
                'absolute right-0 top-5 z-10 flex h-10 min-w-28 max-w-fit items-center justify-center rounded-l-3xl px-2 [&>p]:text-[18px]',
                'tablet:top-3 tablet:h-8 tablet:min-w-20 tablet:[&>p]:text-sm',
                stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_BG,
                stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_TEXT,
                'listpage_badge_card_discount_bg listpage_badge_card_discount_text',
              )}
              discount={discount}
            />
          ) : null}

          {/* ------------------------------------------- Image + Brand --------------------------------------------- */}

          <div className="relative flex w-full flex-col items-center justify-center tablet:mt-5 tablet:w-fit tablet:gap-1">
            <img src={image} alt={name} className="size-56 object-scale-down tablet:h-32 tablet:w-36" />
            <a
              href={redirectUrl}
              onClick={handleCardClick}
              aria-label={`view ${brand}`}
              target="_blank"
              rel="sponsored noreferrer"
              className="flex flex-row items-center gap-2"
            >
              <IconProductBrand className="size- tablet:size-5" />
              <p
                className="line-clamp-1 text-2xl font-normal capitalize tablet:text-base tablet:font-medium"
                data-testid="category-card-brand"
              >
                {brand}
              </p>
            </a>
          </div>

          <div
            className={clsx(
              'ml-auto flex w-56 min-w-74 flex-col items-center justify-center gap-6 border-l border-l-gray-400 px-8',
              'tablet:ml-0 tablet:w-full tablet:gap-1 tablet:border-none',
              showBuyingOptionsCta ? 'tablet:min-w-full tablet:px-2' : 'tablet:min-w-fit tablet:px-4',
            )}
          >
            {/* ------------------------------------------- CTA Desktop --------------------------------------------- */}
            {showBuyingOptionsCta ? (
              <ListingsCta
                product={product}
                index={index + 1}
                className="flex flex-row gap-1.5 p-0.5"
                placement="top-picks-card"
                showProductPageLinks={showProductPageLinks}
              />
            ) : (
              <DefaultCta
                redirectUrl={redirectUrl}
                handleCardClick={handleCardClick}
                brand={brand}
                productScore={productScore}
                index={index}
                showPrimeDayLogo={!!discount}
              />
            )}
          </div>
        </div>

        {/* -------------------------------------------  CUSTOMER HIGHLIGHTS MOBILE --------------------------------------------- */}

        <CustomerHighlightsBeyond
          className="mt-5 desktop:hidden"
          redirectUrl={redirectUrl}
          handleCardClick={handleCardClick}
          brand={brand}
          ratingCount={numOfRatings ?? undefined}
          score={productScore}
          showBottomCta={routeName !== 'product_page' && !showBuyingOptionsCta}
          chosenBy={index === 0 ? chosenBy : undefined}
          cardPlacement={index + 1}
          customerHighlights={customerHighlights}
        />

        {/* -------------------------------------------Brief --------------------------------------------- */}

        <div className="mt-3 flex flex-col gap-7">
          {brief?.map((breafPart, i) => (
            <Fragment key={`${breafPart.slice(0, 10)}-${i}`}>
              <p className="text-lg font-light text-carbon-900 tablet:text-base">{breafPart}</p>
            </Fragment>
          ))}
        </div>

        {/* -------------------------------------------  CTA  --------------------------------------------- */}

        {showBuyingOptionsCta ? null : (
          <div className="flex w-64 flex-col gap-3">
            <a
              href={redirectUrl}
              onClick={handleCardClick}
              aria-label={`view ${brand}`}
              target="_blank"
              rel="sponsored noreferrer"
              className={clsx(
                'relative flex h-[50px] w-full flex-row items-center justify-center gap-8 rounded-3xl px-3',
                stylesBydomain.LISTPAGE_BUTTON_NEW_LAYOUT_PRIMARY,
                'tablet:h-11',
                'listpage_button_new_layout_primary',
              )}
            >
              <div
                className={clsx(
                  'absolute left-3 top-[13px] h-[25px] shrink-0 items-center justify-start rounded-2xl px-2 py-0.5',
                  stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_BG,
                  stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_ICON,
                  stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_TEXT,
                  'tablet:top-2.5',
                  'listpage_badge_cta_marketplace_logo_bg listpage_badge_cta_marketplace_logo_text listpage_badge_cta_marketplace_logo_icon',
                )}
              >
                <AmazonLogoIcon
                  className={clsx(
                    'mt-1 h-4 shrink-0',
                    domainName === 'bestestaubsauger' ||
                      (domainName === 'bestexpertschoice' && locale.toLowerCase() === 'ca')
                      ? 'w-14'
                      : 'w-11',
                  )}
                  domainName={domainName}
                />
              </div>
              <p className="ml-4 text-base font-semibold capitalize tablet:ml-5">
                {translations.ProductCard.checkPrice}
              </p>
            </a>
            {!!discount && (
              <SaleEventBadge
                redirectUrl={redirectUrl}
                handleCardClick={handleCardClick}
                brand={brand ?? ''}
                index={index}
                placement="top-picks-card"
              />
            )}
          </div>
        )}

        <div className="mt-5 border-b border-gray-300 tablet:hidden" />
      </div>
      {/* -------------------------------------------  CUSTOMER HIGHLIGHTS DESKTOP --------------------------------------------- */}
      <CustomerHighlightsBeyond
        className="tablet:hidden"
        redirectUrl={redirectUrl}
        handleCardClick={handleCardClick}
        brand={brand}
        ratingCount={numOfRatings ?? undefined}
        score={productScore}
        showBottomCta={routeName !== 'product_page'}
        chosenBy={index === 0 ? chosenBy : undefined}
        cardPlacement={index + 1}
        customerHighlights={customerHighlights}
      />
    </>
  )
}

export default TopPicksUpdatedCard
